import { Fragment, KeyboardEvent, ReactNode, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { getPivotRouteDayDetail, getPivotRouteDays } from "../../services/pivot";
import {
  ROUTE_DAY_BUCKETS,
  RouteDayBucket,
  RouteDayDetailResponse,
  RouteDayRow,
  RouteDaysResponse,
  RouteDayStop,
  RouteDayStopTag,
  RouteDayThreshold,
} from "../../types/pivot.type";
import { formatThaiShortDate } from "../../utils/date";
import { PivotFilterState } from "./PivotFilterBar";
import { PivotCard } from "./PivotOverview";
import { PivotBarButton } from "./PivotToolbar";
import PivotRouteDayTimeline from "./PivotRouteDayTimeline";
import { CodeChip, fmtMoney, fmtQty } from "./pivotFormat";
import {
  PIVOT_ROUTE_TIME_HO_MAX_DAYS,
  PIVOT_ROUTE_TIME_MAX_DAYS,
  ROUTE_TIME_HO_SPAN_MSG,
  routeTimeSpanBlock,
  usePivotBranchLocked,
} from "./pivotLimits";
import { PV, cardSx } from "./pivotTokens";
import { fmtClock, fmtDistance, fmtMinutes, minutesFromDayStart } from "./routeTimeFormat";

const ROUTE_TIME_MAX_DAYS = PIVOT_ROUTE_TIME_MAX_DAYS;

const SPAN_BLOCKED_MSG =
  `แท็บ “เวลาวิ่งสาย” ดูได้ครั้งละไม่เกิน ${ROUTE_TIME_MAX_DAYS} วัน — ` +
  `ย่อช่วงวันที่ให้ไม่เกิน ${ROUTE_TIME_MAX_DAYS} วัน แล้วลองใหม่`;

const BUCKET_LABEL: Record<RouteDayBucket, string> = {
  "<30": "< 30 ม.",
  "30-200": "30–200 ม.",
  "200-1000": "200 ม.–1 กม.",
  "1000-5000": "1–5 กม.",
  "5000+": "5 กม. ขึ้นไป",
};

const FLAG_TAGS: RouteDayStopTag[] = ["GAP", "APP"];
const isFlaggedStop = (s: RouteDayStop) => s.tags.some((t) => FLAG_TAGS.includes(t));
const routeDayFlagCount = (r: RouteDayRow) =>
  r.gap_flag_count + r.app_flag_count;

// ---- small building blocks --------------------------------------------------

const TH = {
  bgcolor: PV.head,
  color: PV.tx2,
  fontWeight: 500,
  fontSize: 12.5,
  padding: "11px 12px",
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${PV.line}`,
  position: "sticky",
  top: 0,
  zIndex: 2,
} as const;

const TD = {
  padding: "10px 12px",
  borderBottom: `1px solid ${PV.line2}`,
  fontSize: 13,
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
  verticalAlign: "middle",
} as const;

const Dash = () => (
  <Box component="span" sx={{ color: PV.tx3 }}>
    –
  </Box>
);

const Pill = ({ children, fg, bg, title }: { children: ReactNode; fg: string; bg: string; title?: string }) => (
  <Box
    component="span"
    title={title}
    sx={{
      display: "inline-block",
      fontSize: 11.5,
      fontWeight: 600,
      px: 1,
      py: "1px",
      borderRadius: "999px",
      bgcolor: bg,
      color: fg,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

function Segmented<T extends string | boolean>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: ReactNode; tone?: "flag" }[];
  onChange: (v: T) => void;
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        border: "1px solid",
        borderColor: PV.line,
        borderRadius: PV.radiusSm,
        overflow: "hidden",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        const flag = o.tone === "flag";
        return (
          <Box
            key={String(o.value)}
            component="button"
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            sx={{
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              border: 0,
              px: 1.75,
              py: 0.9,
              cursor: "pointer",
              bgcolor: active ? (flag ? PV.leftoverTint : PV.indigoTint) : PV.card,
              color: active ? (flag ? PV.leftover : PV.indigo) : PV.tx2,
              "&:hover": { bgcolor: active ? undefined : PV.rowHover },
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}

const Tile = ({
  label,
  value,
  unit,
  sub,
  color,
  loading,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  color?: string;
  loading?: boolean;
}) => (
  <Box sx={{ ...cardSx, px: 2.25, py: 1.75, minWidth: 0 }}>
    <Typography sx={{ fontSize: 12.5, color: PV.tx2 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
        mt: 0.5,
        color: color ?? PV.tx,
        whiteSpace: "nowrap",
      }}
    >
      {loading ? <Skeleton width={70} /> : value}
      {unit && !loading && (
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: PV.tx2, ml: 0.6 }}>
          {unit}
        </Typography>
      )}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 12, color: PV.tx2, mt: 0.25 }}>{loading ? <Skeleton /> : sub}</Typography>
    )}
  </Box>
);

const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined ? "–" : v.toFixed(1);

// ---- A1 · list -------------------------------------------------------------

const LIST_COLUMNS: { label: string; align?: "right" }[] = [
  { label: "สายส่ง" },
  { label: "วันที่" },
  { label: "ออกสาขา" },
  { label: "ร้านแรก" },
  { label: "ร้านสุดท้าย" },
  { label: "เริ่มตรวจนับ" },
  { label: "ร้าน เข้า/แผน", align: "right" },
  { label: "รอบต่อร้าน (มัธยฐาน)", align: "right" },
  { label: "ระยะรวม", align: "right" },
  { label: "ทำรายการ (มัธยฐาน)", align: "right" },
  { label: "ธงผิดปกติ", align: "right" },
  { label: "นาทีเกินรวม", align: "right" },
  { label: "", align: "right" },
];

function RouteDayListTable({
  data,
  loading,
  collapsed,
  onToggleGroup,
  onOpen,
}: {
  data: RouteDaysResponse | null;
  loading: boolean;
  collapsed: Set<string>;
  onToggleGroup: (key: string) => void;
  onOpen: (row: RouteDayRow, branch: { code: string; name: string | null }) => void;
}) {
  const groups = data?.groups ?? [];
  const cols = LIST_COLUMNS.length;

  return (
    <Box sx={{ overflowX: "auto", maxHeight: 640, overflowY: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box component="thead">
          <Box component="tr">
            {LIST_COLUMNS.map((c, i) => (
              <Box
                component="th"
                key={i}
                sx={{ ...TH, textAlign: c.align ?? "left", pl: i === 0 ? "16px" : undefined }}
              >
                {c.label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <Box component="tr" key={i}>
                {Array.from({ length: cols }).map((__, j) => (
                  <Box component="td" key={j} sx={TD}>
                    <Skeleton variant="text" />
                  </Box>
                ))}
              </Box>
            ))}

          {!loading && groups.length === 0 && (
            <Box component="tr">
              <Box component="td" colSpan={cols} sx={{ ...TD, borderBottom: 0 }}>
                <Box sx={{ py: 5, textAlign: "center", color: PV.tx3 }}>
                  <Typography sx={{ fontWeight: 600, color: PV.tx2, mb: 0.5 }}>
                    ไม่มีข้อมูลเวลาวิ่งสายตามตัวกรอง
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    แท็บนี้มีเฉพาะสายที่ออกบิลจาก PWA · ลองเปลี่ยนวันที่หรือสาขา
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {!loading &&
            groups.map((g) => {
              const isCollapsed = collapsed.has(g.branch_code);
              const shops = g.rows.reduce((n, r) => n + r.shops_visited, 0);
              const flags = g.rows.reduce((n, r) => n + routeDayFlagCount(r), 0);
              const routes = new Set(g.rows.map((r) => r.route_code)).size;
              return (
                <Fragment key={g.branch_code}>
                  <Box
                    component="tr"
                    onClick={() => onToggleGroup(g.branch_code)}
                    sx={{
                      cursor: "pointer",
                      "& > td": {
                        bgcolor: PV.head,
                        borderTop: `1px solid ${PV.line}`,
                        borderBottom: `1px solid ${PV.line2}`,
                        padding: "10px 16px",
                      },
                      "&:hover > td": { bgcolor: "#EDEFF6" },
                    }}
                  >
                    <Box component="td" colSpan={cols}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: 15,
                          color: PV.tx3,
                          fontSize: 10,
                          transition: "transform .12s",
                          transform: isCollapsed ? "rotate(-90deg)" : "none",
                        }}
                      >
                        ▾
                      </Box>
                      <Box component="span" sx={{ fontWeight: 700, fontSize: 13.5 }}>
                        {g.branch_code}
                        {g.branch_name ? ` · ${g.branch_name}` : ""}
                      </Box>
                      <Box component="span" sx={{ color: PV.tx2, fontSize: 12.5, ml: 1.25 }}>
                        {fmtQty(routes)} สาย · {fmtQty(g.rows.length)} สาย-วัน · {fmtQty(shops)} ร้าน ·
                        ธง {fmtQty(flags)}
                      </Box>
                    </Box>
                  </Box>

                  {!isCollapsed &&
                    g.rows.map((r) => {
                      const flagCount = routeDayFlagCount(r);
                      const excess = r.excess_min ?? 0;
                      const open = () => onOpen(r, { code: g.branch_code, name: g.branch_name });
                      return (
                        <Box
                          component="tr"
                          key={`${r.route_code}|${r.date}`}
                          tabIndex={0}
                          role="button"
                          aria-label={`ดูไทม์ไลน์สาย ${r.route_code} วันที่ ${r.date}`}
                          onClick={open}
                          onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              open();
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            outline: "none",
                            "&:hover > td, &:focus-visible > td": { bgcolor: PV.indigoTint },
                          }}
                        >
                          <Box component="td" sx={{ ...TD, pl: "31px" }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {r.route_code}
                            </Box>
                            {r.route_name && r.route_name !== r.route_code && (
                              <Box component="span" sx={{ color: PV.tx3, fontSize: 12, ml: 0.75 }}>
                                {r.route_name}
                              </Box>
                            )}
                          </Box>
                          <Box component="td" sx={{ ...TD, color: PV.tx2 }}>
                            {formatThaiShortDate(r.date) || r.date}
                          </Box>
                          <Box component="td" sx={TD}>
                            {fmtClock(r.leave_at)}
                            {r.leave_at_first_shop && (
                              <Box component="span" sx={{ ml: 0.75 }}>
                                <Pill
                                  fg={PV.leftover}
                                  bg={PV.leftoverTint}
                                  title="กดเช็คอินออกสาขาตอนถึงร้านแรกแล้ว"
                                >
                                  ที่ร้านแรก
                                </Pill>
                              </Box>
                            )}
                          </Box>
                          <Box component="td" sx={TD}>
                            {fmtClock(r.first_check_in)}
                          </Box>
                          <Box component="td" sx={TD}>
                            {fmtClock(r.last_check_in)}
                          </Box>
                          <Box component="td" sx={TD}>
                            {fmtClock(r.count_started_at)}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right" }}>
                            {r.shops_visited}/{r.shops_planned ?? "–"}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right" }}>
                            {fmtMinutes(r.cycle_median_min)}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right" }}>
                            {r.distance_km === null ? <Dash /> : `${r.distance_km.toFixed(1)} กม.`}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right" }}>
                            {fmtMinutes(r.app_median_min)}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right" }}>
                            {flagCount > 0 ? (
                              <Pill
                                fg={PV.leftover}
                                bg={PV.leftoverTint}
                                title={`ช่วงห่างเกิน ${r.gap_flag_count} · ทำรายการนาน ${r.app_flag_count}`}
                              >
                                {flagCount}
                              </Pill>
                            ) : (
                              <Dash />
                            )}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              ...TD,
                              textAlign: "right",
                              fontWeight: 600,
                              color: excess >= 30 ? PV.leftover : excess > 0 ? PV.tx : PV.tx3,
                            }}
                          >
                            {excess > 0 ? `+${excess.toFixed(1)} น.` : "–"}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right", color: PV.indigo, fontWeight: 600 }}>
                            ไทม์ไลน์ ›
                          </Box>
                        </Box>
                      );
                    })}
                </Fragment>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
}

// ---- thresholds + definitions ---------------------------------------------

const DEFINITIONS: [string, string][] = [
  ["รอบต่อร้าน", "เวลาเช็คอินร้านนี้ − เวลาเช็คอินร้านก่อนหน้า (แยกเวลาในร้านกับเวลาเดินทางไม่ได้ เพราะไม่มีสัญญาณ “ถึงร้าน”)"],
  ["ช่วงห่าง", "เวลาเช็คอินร้านนี้ − เวลาออกจากร้านก่อน (พิมพ์บิลต้นฉบับครั้งแรก ถ้าไม่มีใช้เวลาบิล) · ร้านแรกนับจากเวลาออกสาขา แสดงเป็น “จากสาขา” และไม่ติดธง"],
  ["ช่วงห่างผิดปกติ", "เกินเกณฑ์ p95 ของช่วงระยะทางเดียวกันของสาขา (คำนวณจาก 28 วันล่าสุด ถ้าตัวอย่างน้อยกว่า 30 ใช้ค่ากลางทั้งบริษัท) · ไม่นับพักเที่ยง"],
  ["พักเที่ยง", "ช่วงห่างที่ยาวที่สุดของสาย-วันที่เช็คอินระหว่าง 10:30–13:30 · ติดธงเฉพาะเมื่อเกิน 60 นาที"],
  ["ทำรายการ", "เวลาบิลแรก − เวลาเช็คอิน · ติดธง “ทำรายการนาน” เมื่อเกิน 7 นาที · ต่ำกว่า 30 วินาที นับเป็นเช็คอินหลังทำงานเสร็จ"],
  ["ลำดับแผน", "ยังไม่วัดการวิ่งตามลำดับแผน — ตอนนี้การวิ่งยังไม่ต้องตามลำดับแผน"],
  ["นาทีเกินรวม", "ผลรวมของ (ช่วงห่าง − ค่าปกติ p50) ของทุกช่วงที่ติดธงช่วงห่างผิดปกติ"],
  ["ระยะทาง", "เส้นตรงระหว่างพิกัดเช็คอินของร้านที่ต่อกัน · ไม่นับพิกัด 0,0 หรือร้านที่ไม่มีพิกัด"],
  ["เริ่มตรวจนับ", "เวลาเริ่มตรวจนับของที่สาขา ไม่ใช่เวลารถถึงสาขา"],
];

function ThresholdsAndDefinitions({ thresholds }: { thresholds: RouteDayThreshold[] }) {
  const [open, setOpen] = useState(false);
  const byBranch = useMemo(() => {
    const m = new Map<string, Map<RouteDayBucket, RouteDayThreshold>>();
    thresholds.forEach((t) => {
      if (!m.has(t.branch_code)) m.set(t.branch_code, new Map());
      m.get(t.branch_code)!.set(t.bucket, t);
    });
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [thresholds]);

  return (
    <PivotCard
      title="เกณฑ์ช่วงห่างและคำอธิบาย"
      sub="ค่าปกติ (p50) / เกณฑ์ติดธง (p95) เป็นนาที แยกตามระยะทางระหว่างร้าน"
      action={
        <PivotBarButton onClick={() => setOpen((o) => !o)}>{open ? "ซ่อน" : "แสดง"}</PivotBarButton>
      }
      flush={!open}
    >
      {open ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ borderCollapse: "collapse", minWidth: 600 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...TH, position: "static", textAlign: "left" }}>
                    สาขา
                  </Box>
                  {ROUTE_DAY_BUCKETS.map((b) => (
                    <Box component="th" key={b} sx={{ ...TH, position: "static", textAlign: "right" }}>
                      {BUCKET_LABEL[b]}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {byBranch.length === 0 && (
                  <Box component="tr">
                    <Box component="td" colSpan={ROUTE_DAY_BUCKETS.length + 1} sx={{ ...TD, color: PV.tx3 }}>
                      ยังไม่มีเกณฑ์สำหรับช่วงนี้
                    </Box>
                  </Box>
                )}
                {byBranch.map(([branch, buckets]) => (
                  <Box component="tr" key={branch}>
                    <Box component="td" sx={{ ...TD, fontWeight: 600 }}>
                      {branch}
                    </Box>
                    {ROUTE_DAY_BUCKETS.map((b) => {
                      const t = buckets.get(b);
                      return (
                        <Box component="td" key={b} sx={{ ...TD, textAlign: "right" }}>
                          {t ? (
                            <Box
                              title={
                                t.is_fallback
                                  ? `ตัวอย่างของสาขามี ${t.sample_count} ครั้ง ไม่ถึง 30 — ใช้ค่ากลางทั้งบริษัท`
                                  : `จาก ${fmtQty(t.sample_count)} ช่วงห่าง`
                              }
                            >
                              {t.p50.toFixed(1)} / {t.p95.toFixed(1)}
                              <Box component="span" sx={{ color: PV.tx3, fontSize: 11, ml: 0.5 }}>
                                {t.is_fallback ? "ค่ากลาง" : `n=${fmtQty(t.sample_count)}`}
                              </Box>
                            </Box>
                          ) : (
                            <Dash />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box
            component="dl"
            sx={{
              m: 0,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "140px 1fr" },
              columnGap: 2,
              rowGap: 0.75,
              fontSize: 12.5,
              lineHeight: 1.6,
            }}
          >
            {DEFINITIONS.map(([term, def]) => (
              <Fragment key={term}>
                <Box component="dt" sx={{ fontWeight: 600, color: PV.tx }}>
                  {term}
                </Box>
                <Box component="dd" sx={{ m: 0, color: PV.tx2 }}>
                  {def}
                </Box>
              </Fragment>
            ))}
          </Box>
        </Box>
      ) : null}
    </PivotCard>
  );
}

// ---- A2 · one route-day ------------------------------------------------------

const TAG_STYLE: Record<RouteDayStopTag, { fg: string; bg: string; label: string }> = {
  GAP: { fg: PV.leftover, bg: PV.leftoverTint, label: "ช่วงห่างเกิน" },
  APP: { fg: PV.waste, bg: PV.wasteTint, label: "ทำรายการนาน" },
  LUNCH: { fg: PV.tx2, bg: PV.line2, label: "พักเที่ยง" },
  NO_SALE: { fg: PV.tx2, bg: PV.line2, label: "ไม่มีบิล" },
  NO_GPS: { fg: PV.tx3, bg: PV.line2, label: "ไม่มีพิกัด" },
};

const tagLabel = (tag: RouteDayStopTag, s: RouteDayStop) =>
  tag === "GAP" && s.gap_min !== null && s.normal_min !== null
    ? `ช่วงห่างเกิน +${Math.max(0, s.gap_min - s.normal_min).toFixed(1)} น.`
    : TAG_STYLE[tag].label;

const STOP_COLUMNS: { label: string; align?: "right" }[] = [
  { label: "ลำดับจริง" },
  { label: "ร้าน" },
  { label: "เช็คอิน" },
  { label: "ทำรายการ", align: "right" },
  { label: "ห่างจากร้านก่อน", align: "right" },
  { label: "ระยะทาง", align: "right" },
  { label: "ปกติ / เกณฑ์ p95", align: "right" },
  { label: "สถานะ" },
  { label: "ยอดขาย", align: "right" },
];

function RouteDayDetailView({
  routeCode,
  date,
  branch,
  onBack,
  onDateChange,
}: {
  routeCode: string;
  date: string;
  branch: { code: string; name: string | null };
  onBack: () => void;
  onDateChange: (date: string) => void;
}) {
  const [data, setData] = useState<RouteDayDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: res } = await getPivotRouteDayDetail({ route_code: routeCode, date });
        if (!cancelled) setData(res.data);
      } catch {
        // Toasted by the interceptor; the empty state below covers the screen.
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeCode, date]);

  const header = data?.header;
  const stops = useMemo(() => [...(data?.stops ?? [])].sort((a, b) => a.seq - b.seq), [data]);
  const flagged = stops.filter(isFlaggedStop);
  const shown = onlyFlagged ? flagged : stops;
  const isToday = !dayjs(date).isBefore(dayjs(), "day");

  const firstToLast = (() => {
    if (!header) return null;
    const a = minutesFromDayStart(header.first_check_in, header.date);
    const b = minutesFromDayStart(header.last_check_in, header.date);
    return a !== null && b !== null ? (b - a) / 60 : null;
  })();
  const leaveToFirst = (() => {
    if (!header) return null;
    const a = minutesFromDayStart(header.leave_at, header.date);
    const b = minutesFromDayStart(header.first_check_in, header.date);
    return a !== null && b !== null ? b - a : null;
  })();

  return (
    <Box>
      <Box
        sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, color: PV.tx2 }}>
            <Box
              component="button"
              type="button"
              onClick={onBack}
              sx={{
                font: "inherit",
                color: PV.indigo,
                bgcolor: "transparent",
                border: 0,
                p: 0,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              เวลาวิ่งสาย
            </Box>{" "}
            › {branch.code}
            {branch.name ? ` · ${branch.name}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em" }}>
            สาย {routeCode} · {formatThaiShortDate(date) || date}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PivotBarButton onClick={onBack}>← กลับไปตารางสาย-วัน</PivotBarButton>
          <PivotBarButton onClick={() => onDateChange(dayjs(date).subtract(1, "day").format("YYYY-MM-DD"))}>
            ‹ วันก่อน
          </PivotBarButton>
          <PivotBarButton
            disabled={isToday}
            onClick={() => onDateChange(dayjs(date).add(1, "day").format("YYYY-MM-DD"))}
          >
            วันถัดไป ›
          </PivotBarButton>
        </Box>
      </Box>

      {!loading && !header ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: PV.radius }}>
          ไม่มีข้อมูลเวลาของสาย {routeCode} ในวันที่นี้ (สายไม่ได้วิ่ง หรือยังไม่ได้ออกบิลจาก PWA)
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 1.25,
              mb: 2,
            }}
          >
            <Tile
              loading={loading}
              label="ออกสาขา"
              value={fmtClock(header?.leave_at)}
              sub={
                header?.leave_at_first_shop
                  ? "กดออกสาขาตอนถึงร้านแรก"
                  : leaveToFirst !== null
                    ? `ถึงร้านแรก ${fmtMinutes(leaveToFirst)}`
                    : undefined
              }
              color={header?.leave_at_first_shop ? PV.leftover : undefined}
            />
            <Tile
              loading={loading}
              label="ร้านแรก – ร้านสุดท้าย"
              value={`${fmtClock(header?.first_check_in)}–${fmtClock(header?.last_check_in)}`}
              sub={firstToLast !== null ? `${firstToLast.toFixed(1)} ชม.` : undefined}
            />
            <Tile
              loading={loading}
              label="เริ่มตรวจนับ"
              value={fmtClock(header?.count_started_at)}
              sub="ไม่ใช่เวลาถึงสาขา"
            />
            <Tile
              loading={loading}
              label="ร้าน เข้า/แผน"
              value={`${header?.shops_visited ?? 0}/${header?.shops_planned ?? "–"}`}
              sub={
                header?.shops_planned
                  ? `ไม่ได้เข้า ${Math.max(0, header.shops_planned - header.shops_visited)} ร้าน`
                  : undefined
              }
            />
            <Tile
              loading={loading}
              label="รอบต่อร้าน (มัธยฐาน)"
              value={fmtMinutes(header?.cycle_median_min)}
              sub="งานในร้าน + เดินทาง"
            />
            <Tile
              loading={loading}
              label="ระยะทางรวม"
              value={header?.distance_km === null || header?.distance_km === undefined ? "–" : `${header.distance_km.toFixed(1)} กม.`}
              sub="เส้นตรงระหว่างร้าน"
            />
            <Tile
              loading={loading}
              label="ธงผิดปกติ"
              value={`${flagged.length} ร้าน`}
              sub={`เกินรวม ${fmtMinutes(header?.excess_min ?? 0)}`}
              color={flagged.length > 0 ? PV.leftover : undefined}
            />
          </Box>

          <PivotCard title="ไทม์ไลน์ของวัน" sub="ชี้ที่แถบเพื่อดูเวลาและระยะทาง · เวลาเป็นเวลาไทย">
            {loading || !header ? (
              <Skeleton variant="rounded" height={150} />
            ) : (
              <PivotRouteDayTimeline header={header} stops={stops} />
            )}
          </PivotCard>

          <PivotCard
            flush
            title="รายร้านตามลำดับที่เข้าจริง"
            action={
              <Segmented
                value={onlyFlagged}
                onChange={setOnlyFlagged}
                options={[
                  { value: false, label: `ทั้งหมด ${stops.length} ร้าน` },
                  { value: true, label: `เฉพาะที่มีธง ${flagged.length}`, tone: "flag" },
                ]}
              />
            }
          >
            <Box sx={{ overflowX: "auto", maxHeight: 720, overflowY: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box component="thead">
                  <Box component="tr">
                    {STOP_COLUMNS.map((c, i) => (
                      <Box
                        component="th"
                        key={c.label}
                        sx={{ ...TH, textAlign: c.align ?? "left", pl: i === 0 ? "16px" : undefined }}
                      >
                        {c.label}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {loading &&
                    Array.from({ length: 8 }).map((_, i) => (
                      <Box component="tr" key={i}>
                        {STOP_COLUMNS.map((c) => (
                          <Box component="td" key={c.label} sx={TD}>
                            <Skeleton variant="text" />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  {!loading && shown.length === 0 && (
                    <Box component="tr">
                      <Box component="td" colSpan={STOP_COLUMNS.length} sx={{ ...TD, color: PV.tx3, textAlign: "center", py: 4 }}>
                        {onlyFlagged ? "ไม่มีร้านที่ติดธงในวันนี้" : "ไม่มีรายการเข้าร้าน"}
                      </Box>
                    </Box>
                  )}
                  {!loading &&
                    shown.map((s) => {
                      const gapFlag = s.tags.includes("GAP");
                      const appFlag = s.tags.includes("APP");
                      return (
                        <Box
                          component="tr"
                          key={`${s.seq}|${s.customer_code}`}
                          sx={{
                            "& > td": { bgcolor: isFlaggedStop(s) ? `${PV.leftoverTint}80` : PV.card },
                            "&:hover > td": { bgcolor: PV.rowHover },
                          }}
                        >
                          <Box component="td" sx={{ ...TD, pl: "16px", fontWeight: 600 }}>
                            {s.seq}
                          </Box>
                          <Box component="td" sx={{ ...TD, whiteSpace: "normal", minWidth: 200 }}>
                            <CodeChip>{s.customer_code}</CodeChip>{" "}
                            {s.customer_name}
                          </Box>
                          <Box component="td" sx={TD}>
                            {fmtClock(s.check_in_at)}
                          </Box>
                          <Box
                            component="td"
                            sx={{ ...TD, textAlign: "right", color: appFlag ? PV.waste : PV.tx, fontWeight: appFlag ? 700 : 400 }}
                          >
                            {fmtMinutes(s.app_min)}
                          </Box>
                          <Box
                            component="td"
                            sx={{ ...TD, textAlign: "right", color: gapFlag ? PV.leftover : PV.tx, fontWeight: gapFlag ? 700 : 400 }}
                          >
                            {fmtMinutes(s.gap_min)}
                            {s.gap_from_branch && s.gap_min !== null && (
                              <Box component="span" sx={{ color: PV.tx3, ml: 0.5 }}>
                                (จากสาขา)
                              </Box>
                            )}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right", color: PV.tx2 }}>
                            {fmtDistance(s.distance_m)}
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right", color: PV.tx2 }}>
                            {s.normal_min !== null && s.threshold_min !== null
                              ? `${s.normal_min.toFixed(1)} / ${s.threshold_min.toFixed(1)} น.`
                              : "–"}
                          </Box>
                          <Box component="td" sx={TD}>
                            <Box sx={{ display: "flex", gap: 0.75 }}>
                              {s.tags.map((tag) => (
                                <Pill key={tag} fg={TAG_STYLE[tag].fg} bg={TAG_STYLE[tag].bg}>
                                  {tagLabel(tag, s)}
                                </Pill>
                              ))}
                            </Box>
                          </Box>
                          <Box component="td" sx={{ ...TD, textAlign: "right" }}>
                            {fmtMoney(s.sales_amount)}
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            </Box>
          </PivotCard>
        </>
      )}
    </Box>
  );
}

// ---- tab root ------------------------------------------------------------------

interface OpenRouteDay {
  routeCode: string;
  date: string;
  branch: { code: string; name: string | null };
}

/**
 * Tab "เวลาวิ่งสาย" — PWA route-day times. A1 lists route-days grouped by branch; a row
 * opens A2, the one-day timeline, in place (breadcrumb + back, no route change) so the list
 * keeps its data and fold state underneath.
 *
 * Uses the page's branch / route / date filters only: customer type and group have no
 * meaning for a van's day, so they are ignored here.
 */
export default function PivotRouteTimeView({ filters }: { filters: PivotFilterState }) {
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [data, setData] = useState<RouteDaysResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [openDay, setOpenDay] = useState<OpenRouteDay | null>(null);

  const dateFrom = filters.dateFrom.format("YYYY-MM-DD");
  const dateTo = filters.dateTo.format("YYYY-MM-DD");
  const branchCode = filters.branchCode !== "all" ? filters.branchCode : undefined;
  const routeCode = filters.routeCode !== "all" ? filters.routeCode : undefined;
  const branchLocked = usePivotBranchLocked();
  const spanBlock = routeTimeSpanBlock(
    branchLocked,
    filters.branchCode,
    filters.dateFrom,
    filters.dateTo,
  );
  const spanBlocked = spanBlock !== null;

  useEffect(() => {
    // The backend refuses this span before touching the database — skip the round trip and
    // drop the last range's rows so they do not sit under the new range's filter bar.
    if (spanBlocked) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: res } = await getPivotRouteDays({
          date_from: dateFrom,
          date_to: dateTo,
          branch_code: branchCode,
          route_code: routeCode,
          only_flagged: onlyFlagged || undefined,
        });
        if (!cancelled) setData(res.data);
      } catch {
        // The shared interceptor already surfaced it; fall back to the empty state.
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, branchCode, routeCode, onlyFlagged, spanBlocked]);

  // A new scope closes an open day — its timeline belongs to the old filters.
  useEffect(() => {
    setOpenDay(null);
  }, [dateFrom, dateTo, branchCode, routeCode]);

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (openDay) {
    return (
      <RouteDayDetailView
        routeCode={openDay.routeCode}
        date={openDay.date}
        branch={openDay.branch}
        onBack={() => setOpenDay(null)}
        onDateChange={(date) => setOpenDay({ ...openDay, date })}
      />
    );
  }

  const s = data?.summary;

  return (
    <Box>
      <Box
        sx={{
          ...cardSx,
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          flexWrap: "wrap",
          px: 2,
          py: 1.5,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 13, color: PV.tx2 }}>แสดง</Typography>
        <Segmented
          value={onlyFlagged}
          onChange={setOnlyFlagged}
          options={[
            { value: false, label: "ทุกสาย" },
            { value: true, label: "เฉพาะสายที่มีธง", tone: "flag" },
          ]}
        />
        <Typography sx={{ ml: "auto", fontSize: 12, color: PV.tx2, maxWidth: 520, lineHeight: 1.5 }}>
          เฉพาะบิลที่ออกจาก PWA · สาขาที่ยังไม่ใช้ PWA จะไม่แสดงในแท็บนี้ · ใช้ตัวกรองสาขา สายส่ง
          และวันที่ (ไม่เกิน {ROUTE_TIME_MAX_DAYS} วัน ถ้าดูทุกสาขาไม่เกิน {PIVOT_ROUTE_TIME_HO_MAX_DAYS} วัน)
          — ประเภท/กลุ่มลูกค้าไม่มีผลกับแท็บนี้
        </Typography>
      </Box>

      {spanBlocked && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: PV.radius }}>
          {spanBlock === "ho" ? ROUTE_TIME_HO_SPAN_MSG : SPAN_BLOCKED_MSG}
        </Alert>
      )}

      {!spanBlocked && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Tile
              loading={loading}
              label="สาย-วัน"
              value={fmtQty(s?.route_day_count ?? 0)}
              sub={
                s
                  ? `ร้านที่เข้า ${fmtQty(s.shop_count)} · มัธยฐาน ${
                      s.shops_median === null ? "–" : fmtQty(s.shops_median)
                    } ร้าน/สาย`
                  : undefined
              }
            />
            <Tile
              loading={loading}
              label="รอบต่อร้าน (มัธยฐาน)"
              value={s?.cycle_median_min === null || s?.cycle_median_min === undefined ? "–" : s.cycle_median_min.toFixed(1)}
              unit="นาที"
              sub="งานในร้าน + เดินทางไปร้านถัดไป"
            />
            <Tile
              loading={loading}
              label="ช่วงห่างผิดปกติ"
              value={fmtQty(s?.gap_flag_count ?? 0)}
              unit={`ครั้ง · ${fmtQty(s?.flagged_route_day_count ?? 0)} สาย-วัน`}
              sub="เกิน p95 ของระยะทางเดียวกัน · ไม่นับพักเที่ยง"
              color={(s?.gap_flag_count ?? 0) > 0 ? PV.leftover : undefined}
            />
            <Tile
              loading={loading}
              label="เช็คอินหลังทำงานเสร็จ"
              value={fmtPct(s?.checkin_after_work_pct)}
              unit="%"
              sub="ทำรายการในแอปไม่ถึง 30 วินาที"
            />
          </Box>

          <PivotCard
            flush
            title="ตารางเวลาวิ่งสาย · รายสาย-วัน"
            sub="กดที่แถวเพื่อดูไทม์ไลน์ของวันนั้น · เวลาเป็นเวลาไทย"
          >
            <RouteDayListTable
              data={data}
              loading={loading}
              collapsed={collapsed}
              onToggleGroup={toggleGroup}
              onOpen={(row, branch) => setOpenDay({ routeCode: row.route_code, date: row.date, branch })}
            />
          </PivotCard>

          <ThresholdsAndDefinitions thresholds={data?.thresholds ?? []} />
        </>
      )}
    </Box>
  );
}
